import { isSupportedLocale, readLocale } from "#/lib/locale";

const compactFormatter = new Intl.NumberFormat("en-US", {
	notation: "compact",
	maximumFractionDigits: 2,
});

const INTL_LOCALE_BY_APP_LOCALE = {
	en: "en-US",
	ko: "ko-KR",
	zh: "zh-CN",
} as const;

export function getCurrentIntlLocale() {
	if (typeof document !== "undefined") {
		const documentLocale = document.documentElement.lang;
		if (isSupportedLocale(documentLocale)) {
			return INTL_LOCALE_BY_APP_LOCALE[documentLocale];
		}
	}

	return INTL_LOCALE_BY_APP_LOCALE[readLocale()];
}

function formatTimestamp(
	timestamp: number | null | undefined,
	options: Intl.DateTimeFormatOptions,
	emptyValue: string,
) {
	if (!timestamp) return emptyValue;
	return new Intl.DateTimeFormat(getCurrentIntlLocale(), options).format(
		timestamp,
	);
}

export function toNumber(value: string | number | null | undefined) {
	if (typeof value === "number") return value;
	if (!value) return 0;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
}

export function getPriceDigits(value: number) {
	if (value >= 10_000) return 1;
	if (value >= 1_000) return 2;
	if (value >= 1) return 3;
	if (value >= 0.01) return 4;
	return 6;
}

export function formatPrice(value: string | number | null | undefined) {
	const numeric = toNumber(value);
	return numeric.toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: getPriceDigits(Math.abs(numeric)),
	});
}

export function formatUsd(value: string | number | null | undefined) {
	const numeric = toNumber(value);
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 2,
	}).format(numeric);
}

export function formatSignedUsd(value: string | number | null | undefined) {
	const numeric = toNumber(value);
	const absolute = formatUsd(Math.abs(numeric));
	if (absolute === formatUsd(0)) return absolute;
	return numeric > 0 ? `+${absolute}` : numeric < 0 ? `-${absolute}` : absolute;
}

export function formatCompactNumber(value: string | number | null | undefined) {
	return compactFormatter.format(toNumber(value));
}

/** Plain decimal string (no thousands grouping) — for editable amount inputs. */
export function formatPlainNumber(value: number, digits: number): string {
	return value.toLocaleString("en-US", {
		useGrouping: false,
		maximumFractionDigits: digits,
	});
}

export function formatUsdc(value: number, token = "USDC") {
	return `${Math.abs(value).toFixed(2)} ${token}`;
}

export function formatSignedUsdc(value: number, token = "USDC") {
	const abs = formatUsdc(value, token);
	return value > 0 ? `+${abs}` : value < 0 ? `-${abs}` : abs;
}

/** Split a camelCase / dash / underscore identifier into a spaced label. */
export function formatCamelDashLabel(value: string): string {
	return value
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/[-_]/g, " ")
		.trim();
}

export function formatKrw(
	usd: string | number | null | undefined,
	rate: number | null | undefined,
): string | null {
	const numericUsd = toNumber(usd);
	if (!rate || !Number.isFinite(rate)) return null;
	const krw = numericUsd * rate;
	const sign = krw < 0 ? "-" : "";
	const formatted = new Intl.NumberFormat("ko-KR", {
		style: "currency",
		currency: "KRW",
		maximumFractionDigits: 0,
	}).format(Math.abs(krw));
	return `≈ ${sign}${formatted}`;
}

export function formatSignedPercent(value: string | number | null | undefined) {
	const numeric = toNumber(value);
	const sign = numeric > 0 ? "+" : "";
	return `${sign}${numeric.toFixed(2)}%`;
}

export function formatFundingRate(value: string | number | null | undefined) {
	const numeric = toNumber(value) * 100;
	const sign = numeric > 0 ? "+" : "";
	return `${sign}${numeric.toFixed(4)}%`;
}

export function formatSize(
	value: string | number | null | undefined,
	digits = 4,
) {
	return toNumber(value).toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: digits,
	});
}

export function formatLeverage(value: string | number | null | undefined) {
	return `${toNumber(value)}x`;
}

export function formatDateTime(timestamp: number | null | undefined) {
	return formatTimestamp(
		timestamp,
		{
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		},
		"--",
	);
}

export function formatClockTime(timestamp: number | null | undefined) {
	return formatTimestamp(
		timestamp,
		{
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false,
		},
		"--:--:--",
	);
}

export function formatDetailedDateTime(timestamp: number | null | undefined) {
	return formatTimestamp(
		timestamp,
		{
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hour12: false,
		},
		"--",
	);
}

export function formatRelativePriceDelta(current: number, previous: number) {
	if (!previous) return 0;
	return ((current - previous) / previous) * 100;
}

/** Return multiple: "1.02x" under 10, "12.4x" under 100, "123x" above. */
export function formatMultiple(value: number): string {
	const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
	return `${value.toFixed(digits)}x`;
}

export function formatCountdown(value: number) {
	const safeValue = Math.max(0, value);
	const minutes = Math.floor(safeValue / 60);
	const seconds = safeValue % 60;
	return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

export function getFundingCountdownSeconds() {
	const now = new Date();
	const nextHour = new Date(now);
	nextHour.setUTCMinutes(0, 0, 0);
	nextHour.setUTCHours(nextHour.getUTCHours() + 1);
	return Math.floor((nextHour.getTime() - now.getTime()) / 1000);
}

export function formatWalletAddress(address: string) {
	return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function maskEmail(email: string) {
	const at = email.lastIndexOf("@");
	if (at <= 0) return email;
	const local = email.slice(0, at);
	const domain = email.slice(at);
	if (local.length <= 2) return `${local[0]}*${domain}`;
	return `${local[0]}${"*".repeat(Math.min(local.length - 2, 4))}${local[local.length - 1]}${domain}`;
}
